<template>
  <v-row>
    <v-col cols="12">
      <v-card class="reseller-management-card">
        <v-card-title class="d-flex align-center">
          Reseller Management
          <v-spacer />
          <v-btn icon color="primary" @click="refreshAll">
            <font-awesome-icon icon="fa-solid fa-sync" />
          </v-btn>
        </v-card-title>

        <v-tabs v-model="tab" background-color="transparent" show-arrows>
          <v-tab>Resellers</v-tab>
          <v-tab>Reseller Sales</v-tab>
        </v-tabs>

        <v-divider />

        <v-tabs-items v-model="tab">
          <!-- Resellers Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-row class="mb-4">
                  <v-col cols="12" md="4">
                    <div class="summary-card">
                      <div class="summary-label">Total Resellers</div>
                      <div class="summary-value">{{ resellerTotals.count }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="summary-card">
                      <div class="summary-label">Total Balance</div>
                      <div class="summary-value">{{ resellerTotals.balance }} XAF</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="summary-card">
                      <div class="summary-label">Total Earned</div>
                      <div class="summary-value">{{ resellerTotals.earned }} XAF</div>
                    </div>
                  </v-col>
                </v-row>

                <div class="d-flex align-center mb-3">
                  <v-text-field
                    v-model="resellerSearch"
                    append-icon="mdi-magnify"
                    label="Search resellers"
                    single-line
                    hide-details
                    dense
                    class="mr-4"
                  />
                  <v-spacer />
                  <v-btn small color="primary" :loading="resellersLoading" @click="loadResellers">
                    Refresh
                  </v-btn>
                </div>

                <v-data-table
                  :headers="resellerHeaders"
                  :items="resellers"
                  :search="resellerSearch"
                  :loading="resellersLoading"
                  :items-per-page="15"
                  class="table-rounded"
                >
                  <template #[`item.created_at`]="{ item }">
                    {{ item.created_at }}
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Reseller Sales Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-row class="mb-3">
                  <v-col cols="12" md="4">
                    <div class="summary-card">
                      <div class="summary-label">Total Base Amount</div>
                      <div class="summary-value">{{ salesTotals.base_amount }} XAF</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="summary-card">
                      <div class="summary-label">Platform Commission</div>
                      <div class="summary-value">{{ salesTotals.commission_amount }} XAF</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="summary-card">
                      <div class="summary-label">Reseller Share</div>
                      <div class="summary-value">{{ salesTotals.reseller_amount }} XAF</div>
                    </div>
                  </v-col>
                </v-row>

                <div class="d-flex align-center mb-3">
                  <v-text-field
                    v-model="salesSearch"
                    append-icon="mdi-magnify"
                    label="Search sales"
                    single-line
                    hide-details
                    dense
                    class="mr-4"
                  />
                  <v-spacer />
                  <v-btn small color="primary" :loading="salesLoading" @click="loadSales">
                    Refresh
                  </v-btn>
                </div>

                <v-data-table
                  :headers="salesHeaders"
                  :items="resellerSales"
                  :search="salesSearch"
                  :loading="salesLoading"
                  :items-per-page="15"
                  class="table-rounded"
                >
                  <template #[`item.reseller`]="{ item }">
                    <v-chip v-if="item.reseller" x-small color="indigo" dark>{{ item.reseller }}</v-chip>
                    <span v-else class="grey--text">—</span>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ManagerResellerManagement',

  data: () => ({
    tab: 0,

    resellers: [],
    resellersLoading: false,
    resellerSearch: '',

    resellerSales: [],
    salesTotals: {
      base_amount: '0',
      commission_amount: '0',
      reseller_amount: '0',
    },
    salesLoading: false,
    salesSearch: '',

    resellerHeaders: [
      { text: 'Username', value: 'username' },
      { text: 'Email', value: 'email' },
      { text: 'Business', value: 'business_name' },
      { text: 'Balance', value: 'reseller_balance' },
      { text: 'Total Earned', value: 'total_earned' },
      { text: 'Created', value: 'created_at' },
    ],

    salesHeaders: [
      { text: 'Order ID', value: 'order_id' },
      { text: 'Product', value: 'product' },
      { text: 'Reseller', value: 'reseller' },
      { text: 'Qty', value: 'quantity' },
      { text: 'Base', value: 'base_amount' },
      { text: 'Platform Commission', value: 'commission_amount' },
      { text: 'Reseller Share', value: 'reseller_amount' },
      { text: 'Date', value: 'created_at' },
    ],
  }),

  computed: {
    resellerTotals() {
      const count = this.resellers.length
      const balance = this.resellers.reduce((sum, r) => sum + (parseFloat(r.reseller_balance) || 0), 0)
      const earned = this.resellers.reduce((sum, r) => sum + (parseFloat(r.total_earned) || 0), 0)
      return { count, balance: Math.round(balance), earned: Math.round(earned) }
    },
  },

  mounted() {
    document.title = 'Manager | Reseller Management'
    this.refreshAll()
  },

  methods: {
    refreshAll() {
      this.loadResellers()
      this.loadSales()
    },

    async loadResellers() {
      this.resellersLoading = true
      try {
        const res = await axios.get('/api/v1/market_place/manager/resellers/')
        this.resellers = res.data
      } catch (err) {
        this.notifyError(err, 'Failed to load resellers.')
      } finally {
        this.resellersLoading = false
      }
    },

    async loadSales() {
      this.salesLoading = true
      try {
        const res = await axios.get('/api/v1/market_place/manager/seller/commission/history/')
        const all = res.data.results || []
        this.resellerSales = all.filter(r => r.reseller)
        this.salesTotals = {
          base_amount: String(Math.round(this.resellerSales.reduce((s, r) => s + (parseFloat(r.base_amount) || 0), 0))),
          commission_amount: String(Math.round(this.resellerSales.reduce((s, r) => s + (parseFloat(r.commission_amount) || 0), 0))),
          reseller_amount: String(Math.round(this.resellerSales.reduce((s, r) => s + (parseFloat(r.reseller_amount) || 0), 0))),
        }
      } catch (err) {
        this.notifyError(err, 'Failed to load reseller sales.')
      } finally {
        this.salesLoading = false
      }
    },

    notifyError(err, fallback) {
      const msg = err.response?.data?.error || err.response?.data?.message || fallback
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', 'red darken-4')
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
.reseller-management-card {
  border-radius: 16px;
}

.summary-card {
  background: #f7f9fc;
  border: 1px solid #e6e9f2;
  border-radius: 12px;
  padding: 14px 16px;
}

.summary-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
}

.summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}
</style>
